import React, { useState, useEffect } from 'react';
import { db } from '../../firebase';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { Package, Clock, CheckCircle2, Truck, Leaf } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function MyOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        // Fetching orders sorted by creation date
        const q = query(collection(db, 'orders'));
        const querySnapshot = await getDocs(q);
        const orderList = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        // Sorting manually or via query if index exists
        orderList.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
        setOrders(orderList);
      } catch (error) {
        console.error("Error fetching customer orders: ", error);
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-5xl mx-auto">
        
        <div className="flex items-center space-x-3 mb-8">
          <div className="bg-ecoGreen p-2.5 rounded-xl text-white">
            <Package className="h-6 w-6" />
          </div>
          <div>
            <h1 className="text-2xl font-extrabold text-gray-900">My Eco-Orders History</h1>
            <p className="text-xs text-gray-500">Track all your sustainable purchases and green delivery status.</p>
          </div>
        </div>

        {loading ? (
          <p className="text-center text-gray-500 py-16">Loading your orders...</p>
        ) : orders.length === 0 ? (
          <div className="bg-white p-12 rounded-xl text-center shadow-sm border border-gray-200">
            <Leaf className="h-12 w-12 text-ecoGreen mx-auto mb-3" />
            <h3 className="font-bold text-gray-800 text-lg mb-1">No orders placed yet</h3>
            <p className="text-sm text-gray-500 mb-6">Start your zero-waste journey by shopping from our curated store.</p>
            <Link 
              to="/shop" 
              className="inline-block bg-ecoGreen hover:bg-ecoGreen-dark text-white px-6 py-2.5 rounded-lg font-medium text-sm transition-colors shadow-xs"
            >
              Explore Eco-Shop
            </Link>
          </div>
        ) : (
          <div className="space-y-6">
            {orders.map((order) => (
              <div key={order.id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                
                {/* Order Top Meta Bar */}
                <div className="bg-gray-50 px-6 py-4 border-b border-gray-200 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 text-xs">
                  <div>
                    <span className="text-gray-500 font-semibold">ORDER ID: </span>
                    <span className="font-mono text-gray-800 font-bold">{order.id}</span>
                  </div>
                  <div className="flex items-center space-x-4">
                    <span className="text-gray-500">
                      {order.createdAt?.seconds ? new Date(order.createdAt.seconds * 1000).toLocaleDateString() : 'Just Now'}
                    </span>
                    <span className="bg-ecoGreen-light text-ecoGreen-dark px-2.5 py-1 rounded-full font-bold flex items-center space-x-1">
                      <Clock className="h-3 w-3" />
                      <span>{order.status || 'Processing'}</span>
                    </span>
                  </div>
                </div>

                {/* Order Items List */}
                <div className="p-6 divide-y divide-gray-100">
                  {order.items?.map((item, index) => (
                    <div key={index} className="py-3 first:pt-0 last:pb-0 flex justify-between items-center text-sm">
                      <div className="flex items-center space-x-3">
                        <div className="h-10 w-10 bg-ecoGreen-light rounded-lg flex items-center justify-center text-ecoGreen font-bold text-xs">
                          🌿
                        </div>
                        <div>
                          <h4 className="font-bold text-gray-800">{item.name}</h4>
                          <p className="text-xs text-gray-500">Qty: {item.quantity} × ₹{item.price}</p>
                        </div>
                      </div>
                      <span className="font-bold text-gray-900">₹{item.price * item.quantity}</span>
                    </div>
                  ))}
                </div>

                {/* Order Footer & Total */}
                <div className="bg-gray-50/50 px-6 py-4 border-t border-gray-200 flex flex-col sm:flex-row justify-between items-center gap-4 text-sm">
                  <div className="flex items-center space-x-2 text-xs text-ecoGreen font-medium">
                    <Truck className="h-4 w-4" />
                    <span>Plastic-Free Delivery • {order.treesPlanted || 0} Trees Planted 🌳</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-gray-600 font-medium">Total Paid:</span>
                    <span className="text-lg font-extrabold text-ecoGreen-dark">₹{order.total}</span>
                  </div>
                </div>

              </div>
            ))}
          </div>
        )}

      </div>
    </div>
  );
}